import React from 'react';

export type ResponseType = 'rating' | 'text' | 'choice' | 'multiple_choice' | 'boolean';

interface QuestionInputProps {
  value: any;
  onChange: (value: any) => void;
  disabled?: boolean;
  choices?: string[];
  required?: boolean;
  questionId?: string; // Add questionId to make radio button names unique
}

// Rating Input Component (1-5 stars)
export const RatingInput: React.FC<QuestionInputProps> = ({ value, onChange, disabled, required }) => {
  const ratings = [1, 2, 3, 4, 5];
  const selectedRating = value ? parseInt(value) : 0;

  return (
    <div className="flex items-center space-x-2">
      {ratings.map((rating) => {
        const isFilled = rating <= selectedRating;
        return (
          <button
            key={rating}
            type="button"
            onClick={() => !disabled && onChange(rating)}
            disabled={disabled}
            className={`
              w-10 h-10 rounded-full border-2 flex items-center justify-center
              transition-colors duration-200
              ${isFilled
                ? 'bg-yellow-400 border-yellow-500 text-yellow-900'
                : 'bg-gray-100 border-gray-300 text-gray-600 hover:bg-gray-200'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
              focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2
            `}
            title={`Rate ${rating} out of 5`}
          >
            <span className="text-lg">⭐</span>
          </button>
        );
      })}
      {required && !value && (
        <span className="text-sm text-red-500">Required</span>
      )}
    </div>
  );
};

// Text Input Component
export const TextInput: React.FC<QuestionInputProps> = ({ value, onChange, disabled, required }) => {
  return (
    <textarea
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      required={required}
      rows={4}
      className={`
        mt-1 block w-full rounded-md border-gray-300 shadow-sm
        focus:border-primary-500 focus:ring-primary-500 sm:text-sm
        ${disabled ? 'bg-gray-100 cursor-not-allowed' : ''}
      `}
      placeholder="Enter your response..."
    />
  );
};

// Choice Input Component (Single Select)
export const ChoiceInput: React.FC<QuestionInputProps> = ({ value, onChange, disabled, choices = [], required, questionId }) => {
  if (!choices || choices.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No choices available
      </div>
    );
  }

  // Use questionId to make radio button name unique per question
  const radioName = questionId ? `choice-${questionId}` : `choice-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="space-y-2">
      {choices.map((choice, index) => (
        <label
          key={index}
          className={`
            flex items-center p-3 rounded-lg border-2 cursor-pointer
            transition-colors duration-200
            ${value === choice
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            type="radio"
            name={radioName}
            value={choice}
            checked={value === choice}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            required={required}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300"
          />
          <span className="ml-3 text-sm text-gray-700">{choice}</span>
        </label>
      ))}
      {required && !value && (
        <p className="text-sm text-red-500">Please select an option</p>
      )}
    </div>
  );
};

// Multiple Choice Input Component (Multi Select)
export const MultipleChoiceInput: React.FC<QuestionInputProps> = ({ value, onChange, disabled, choices = [], required }) => {
  if (!choices || choices.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No choices available
      </div>
    );
  }

  const selectedValues = Array.isArray(value) ? value : [];

  const handleToggle = (choice: string) => {
    if (disabled) return;
    
    const newValues = selectedValues.includes(choice)
      ? selectedValues.filter(v => v !== choice)
      : [...selectedValues, choice];
    
    onChange(newValues);
  };

  return (
    <div className="space-y-2">
      {choices.map((choice, index) => (
        <label
          key={index}
          className={`
            flex items-center p-3 rounded-lg border-2 cursor-pointer
            transition-colors duration-200
            ${selectedValues.includes(choice)
              ? 'border-primary-500 bg-primary-50'
              : 'border-gray-200 hover:border-gray-300'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <input
            type="checkbox"
            checked={selectedValues.includes(choice)}
            onChange={() => handleToggle(choice)}
            disabled={disabled}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
          />
          <span className="ml-3 text-sm text-gray-700">{choice}</span>
        </label>
      ))}
      {required && selectedValues.length === 0 && (
        <p className="text-sm text-red-500">Please select at least one option</p>
      )}
    </div>
  );
};

// Boolean Input Component (Yes/No)
export const BooleanInput: React.FC<QuestionInputProps> = ({ value, onChange, disabled, required, questionId }) => {
  // Use questionId to make radio button name unique per question
  const radioName = questionId ? `boolean-${questionId}` : `boolean-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className="flex items-center space-x-4">
      <label className={`
        flex items-center p-3 rounded-lg border-2 cursor-pointer
        transition-colors duration-200
        ${value === true
          ? 'border-green-500 bg-green-50'
          : 'border-gray-200 hover:border-gray-300'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}>
        <input
          type="radio"
          name={radioName}
          checked={value === true}
          onChange={() => !disabled && onChange(true)}
          disabled={disabled}
          required={required}
          className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300"
        />
        <span className="ml-3 text-sm font-medium text-gray-700">Yes</span>
      </label>
      <label className={`
        flex items-center p-3 rounded-lg border-2 cursor-pointer
        transition-colors duration-200
        ${value === false
          ? 'border-red-500 bg-red-50'
          : 'border-gray-200 hover:border-gray-300'
        }
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
      `}>
        <input
          type="radio"
          name={radioName}
          checked={value === false}
          onChange={() => !disabled && onChange(false)}
          disabled={disabled}
          required={required}
          className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300"
        />
        <span className="ml-3 text-sm font-medium text-gray-700">No</span>
      </label>
      {required && value === undefined && (
        <span className="text-sm text-red-500">Required</span>
      )}
    </div>
  );
};

// Main Question Input Component that renders based on responseType
interface QuestionInputComponentProps extends QuestionInputProps {
  responseType: ResponseType;
}

export const QuestionInput: React.FC<QuestionInputComponentProps> = ({
  responseType,
  value,
  onChange,
  disabled,
  choices,
  required,
  questionId
}) => {
  switch (responseType) {
    case 'rating':
      return <RatingInput value={value} onChange={onChange} disabled={disabled} required={required} questionId={questionId} />;
    case 'text':
      return <TextInput value={value} onChange={onChange} disabled={disabled} required={required} questionId={questionId} />;
    case 'choice':
      return <ChoiceInput value={value} onChange={onChange} disabled={disabled} choices={choices} required={required} questionId={questionId} />;
    case 'multiple_choice':
      return <MultipleChoiceInput value={value} onChange={onChange} disabled={disabled} choices={choices} required={required} questionId={questionId} />;
    case 'boolean':
      return <BooleanInput value={value} onChange={onChange} disabled={disabled} required={required} questionId={questionId} />;
    default:
      return <div className="text-sm text-gray-500">Unknown response type</div>;
  }
};

